import React, { FunctionComponent, ReactNode, useCallback, useState } from 'react'
import { Grid, Text, Avatar, Button, Link, usePhoneNumber } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'

import { useAuthentication, useCashAppConsumerName, useConsumerName, useConsumerSelector, useCountry, useMobileLogin } from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { AppLink, LogoutForm, ProfileCardProps } from '../'
import { Route } from '../../config/router'
import styles from './style.module.css'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import clsx from 'clsx'

export const ProfileCard: FunctionComponent<ProfileCardProps> = ({
  allowLogout = false,
  authLogout = false,
  header,
  onEdit,
  hideSubheader = true,
  customSubheader,
  revealMobile = false,
}) => {
  const { t } = useTranslation()
  const country = useCountry()
  const profileConsumerName = useConsumerName()
  const cashAppConsumerName = useCashAppConsumerName()
  const consumerName = profileConsumerName.length > 0 ? profileConsumerName : cashAppConsumerName
  const { authenticated } = useAuthentication()
  const formatter = usePhoneNumber({ country })
  const { isMobileLogin } = useMobileLogin()

  const { email, mobile } = useConsumerSelector()
  const { formatIntMaskedNumber } = usePhoneNumber({ country })

  const formatNumber = useCallback(
    (mobileNumber) => {
      return authenticated || revealMobile ? formatter.formatInternational(mobileNumber) : formatIntMaskedNumber(mobileNumber)
    },
    [authenticated, formatter, revealMobile, isMobileLogin]
  )

  const { textSize, smallTextSize } = useCheckoutSizes()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [showLogoutForm, setShowLogoutForm] = useState(false)
  const hasOnEdit = typeof onEdit !== 'undefined'
  const [showEditButton, setShowEditButton] = useState(hasOnEdit)
  const mobileHeader = isMobileLogin ? formatNumber(mobile) : null
  const consumerNameHeader = consumerName !== '' ? consumerName : null
  const defaultHeader = customSubheader ?? consumerNameHeader ?? mobileHeader ?? email
  const headerLength = header?.length ?? 0

  const handleEditClick = (): void => {
    hasOnEdit && onEdit()

    setShowEditButton(!showEditButton)
  }

  const onLogoutHandler = (): void => {
    setShowLogoutForm(true)
    logEvent('Pressed Logout Button')
  }

  const onCloseHandler = (): void => {
    setShowLogoutForm(false)
  }

  const renderNotYou = (): JSX.Element => {
    return (
      <Text size={smallTextSize} renderAs='span'>
        <AppLink route={Route.ENTER_EMAIL} noMargin underline>
          {t('common:notYou')}
        </AppLink>
      </Text>
    )
  }

  const renderAuthLogout = (): JSX.Element => {
    return (
      <Text size={smallTextSize} renderAs='span'>
        <TrackOnMount eventName='Viewed Logout Button' />
        <Link kind='Underline' onClick={onLogoutHandler} testNameSpace='profile-card-logout'>
          {t('common:logOut')}
        </Link>
      </Text>
    )
  }

  const renderLink = (): ReactNode => {
    if (allowLogout && authLogout) {
      return renderAuthLogout()
    }
    if (allowLogout) {
      return renderNotYou()
    }

    return null
  }

  const profileTextStyles = clsx(styles.variableColumnWrapper, styles.centerText)

  return (
    <Grid>
      <MaskedElement actionName='Profile Card'>
        <div className={styles.profileCard}>
          <Avatar size='M' src='' alt={consumerName} name={consumerName} title={consumerName} />
          <div className={profileTextStyles}>
            <Text size={textSize} renderAs='div'>
              <div className={styles.variableColumn}>
                <span className={styles.ellipsisText}>
                  <Text size='M' bold testNameSpace='profile-card-header' color='Black' renderAs='span'>
                    {headerLength > 0 ? header : defaultHeader}
                  </Text>
                </span>
                {hasOnEdit && showEditButton && (
                  <div className={styles.editWrapper}>
                    <Button.Link size='Regular' onClick={handleEditClick} testNameSpace='edit-profile'>
                      {t('common:edit')}
                    </Button.Link>
                  </div>
                )}
              </div>
              <div className={styles.variableColumn}>
                {renderLink()}
                {!hideSubheader && headerLength > 0 && !allowLogout && (
                  <span className={styles.wrapText}>
                    <Text size={smallTextSize} testNameSpace='profile-sub-header' renderAs='span' color='Gray50'>
                      {defaultHeader}
                    </Text>
                  </span>
                )}
              </div>
            </Text>
          </div>
          {showLogoutForm && (
            <>
              <LogoutForm onClose={onCloseHandler} />
            </>
          )}
        </div>
      </MaskedElement>
    </Grid>
  )
}
